import { Pause, Play } from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "../../types/message";
import { MessageBubble } from "./message-bubble";

const formatTime = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return "0:00";

  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      seconds,
    ).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export default function AudioMessage({ message }: { message: Message }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
  }, [message.url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
  }, []);

  const formattedCurrentTime = useMemo(
    () => formatTime(currentTime),
    [currentTime],
  );
  const formattedDuration = useMemo(() => formatTime(duration), [duration]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    if (audio.paused) {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        setHasError(true);
      }
      return;
    }

    audio.pause();
    setIsPlaying(false);
  };

  const handleSeek = (event: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    const nextTime = Number(event.target.value);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  if (!message.url) return null;

  return (
    <MessageBubble message={message}>
      <div className="w-full rounded-xl border border-slate-200 bg-gradient-to-br from-white via-white to-slate-50 p-2 shadow-sm">
        <div className="flex flex-row gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pausar audio" : "Reproduzir audio"}
            disabled={hasError}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>

          <div className="min-w-0 flex-1">
            <input
              type="range"
              min={0}
              max={duration || 1}
              step={0.1}
              value={Math.min(currentTime, duration || currentTime)}
              onChange={handleSeek}
              aria-label="Linha do tempo do audio"
              disabled={hasError || duration === 0}
              className="w-full cursor-pointer accent-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>{formattedCurrentTime}</span>
              <span>{formattedDuration}</span>
            </div>
          </div>
        </div>

        {hasError ? (
          <div className="mt-2 text-xs text-red-600">
            Nao foi possivel reproduzir o audio.{" "}
            <a
              href={message.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-700 hover:underline"
            >
              Abrir arquivo
            </a>
          </div>
        ) : null}
      </div>

      <audio
        ref={audioRef}
        key={message.url}
        preload="metadata"
        playsInline
        onLoadedMetadata={(event) => {
          const nextDuration = event.currentTarget.duration;
          setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
          setHasError(false);
        }}
        onTimeUpdate={(event) => {
          const nextTime = event.currentTarget.currentTime;
          setCurrentTime(Number.isFinite(nextTime) ? nextTime : 0);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onError={() => setHasError(true)}
      >
        <source src={message.url} type={message.contentType || "audio/ogg"} />
        Seu navegador nao suporta audio.
      </audio>
    </MessageBubble>
  );
}
